import React, { useEffect, useState } from "react";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { useHeaderContext } from "../../../context/headerContext";
import { useUserContext } from "../../../context/userContext";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import CheckoutForm from "../../../components/CheckoutForm";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

const initStripe = async () => {
  const { user, logout, updateTokens } = useUserContext();

  try {
    return await Sentry.startSpan({ name: "Fetch Stripe Publishable Key", op: "http.request" }, async (span) => {
      const url = `${import.meta.env.VITE_API_URL}Stripe/FetchPublishableKey`;
      span.setAttribute("http.url", url);
      span.setAttribute("userId", user?.id);

      const response = await fetchWithRefresh(
        url,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.token}`,
          },
        },
        logout,
        updateTokens,
        (message) => {
          toast.info(message);
        }
      );

      if (response.ok) {
        const data = await response.json();
        const publishableKey = data.PublishableKey;
        span.setAttribute("stripe.publishableKey", publishableKey);
        return loadStripe(publishableKey);
      } else {
        throw new Error("Error fetching Stripe publishable key");
      }
    });
  } catch (error) {
    Sentry.captureException(error, { extra: { userId: user?.id } });
    LogErrors(user, logout, updateTokens, "Error fetching Stripe publishable key: " + error);
  }
};

const stripeCheckoutStyles = {
  variables: {
      fontFamily: 'Poppins, sans-serif',
      fontSizeSm: '16px',
      fontLineHeight: '26px',
      colorPrimaryText: '#101820',
      colorTextSecondary: '#101820',
      borderRadius: '4px',
      borderWidth: '1px',
      colorBackground: '#F8F8F8',
      colorComponentBorder: '#E3E3E3',
      colorComponentText: '#101820',
      colorTextPlaceholder: '#727272',
      colorDanger: '#DF3327',
      colorWarning: '#DF3327',
      colorSuccess: '#47B405',
      spacingGridRow: '24px',
      gridColumnSpacing: '24px',
      iconCardCvcColor: '#6D6E77',
      iconChevronDownColor: '#000000',
      iconChevronDownHoverColor: '#000000',
  },
};

export default function ShopCheckout() {
  const { setTitle, setBackButton, setCartButton, setCartButtonFunc } = useHeaderContext();
  const { user, logout, updateTokens } = useUserContext();
  const [cartPrice, setCartPrice] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const priceId = queryParams.get('priceId') || false;
  const purchasedSubscriptionId = queryParams.get('purchasedSubscriptionId') || false;

  const fetchCart = async () => {
    try {
      return await Sentry.startSpan({ name: "Fetch Cart Data", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Cart/Cart?userAccountId=${user.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => {
            toast.info(message);
          }
        );
  
        if (response.ok) {
          const data = await response.json();
          setCartPrice(data?.CartTotal);
          span.setAttribute("cart.totalPrice", data?.CartTotal);
        } else {
          throw new Error("Failed to fetch cart");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id } });
      LogErrors(user, logout, updateTokens, "Error fetching cart: " + error);
    }
  };

  useEffect(() => {
    setTitle("CHECKOUT");
    setBackButton(true);
    setCartButton(true);

    const handleBack = () => {
      navigate('/cart');
    };

    setCartButtonFunc(handleBack);
    fetchCart();
  }, []);

  const stripePromise = initStripe();
  const [clientSecretSettings, setClientSecretSettings] = useState({
    clientSecret: "",
    loading: true,
  });

  async function createSubscriptionIntent() {
    try {
      return await Sentry.startSpan({ name: "Fetch Subscription Payment Intent", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Stripe/FetchPWASubscriptionPaymentIntent/?userAccountId=${user?.id}&priceId=${priceId}&returnUrl=${import.meta.env.VITE_PWA_URL}/shop/checkout/success/?purchasedSubscriptionId=${purchasedSubscriptionId}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
        span.setAttribute("priceId", priceId);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (response.ok) {
          const data = await response.json();
          setClientSecretSettings({
            clientSecret: data.clientSecret,
            loading: false,
          });
          span.setAttribute("payment.clientSecret", data.clientSecret);
        } else {
          throw new Error("Error fetching subscription payment intent");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id, priceId } });
      LogErrors(user, logout, updateTokens, "Error fetching subscription payment intent: " + error);
    }
  }

  async function createPaymentIntent() {
    try {
      return await Sentry.startSpan({ name: "Fetch Payment Intent", op: "http.request" }, async (span) => {
        const url = `${import.meta.env.VITE_API_URL}Stripe/FetchPaymentIntent/?amount=${cartPrice}&userAccountId=${user?.id}`;
        span.setAttribute("http.url", url);
        span.setAttribute("userId", user?.id);
        span.setAttribute("cartPrice", cartPrice);
  
        const response = await fetchWithRefresh(
          url,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        if (response.ok) {
          const data = await response.json();
          setClientSecretSettings({
            clientSecret: data.PaymentIntentClientSecret,
            loading: false,
          });
          span.setAttribute("payment.clientSecret", data.PaymentIntentClientSecret);
        } else {
          throw new Error("Error fetching payment intent");
        }
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { userId: user?.id, cartPrice } });
      LogErrors(user, logout, updateTokens, "Error fetching payment intent: " + error);
    }
  }  

  useEffect(() => {
      if(priceId){
        createSubscriptionIntent();
      } else if(cartPrice !== 0){
        createPaymentIntent();
      }
  }, [cartPrice, priceId]);

  return (
    <div>
      <>
          {clientSecretSettings.loading ? (
              <h1>Loading ...</h1>
          ) : (
              <div className="pb-[50px]">
                  {!priceId && (
                    <div className='flex flex-col gap-1 pb-6 border-dashed border-b border-[#E3E3E3] mb-6'>
                        <h1 className="text-[12px] leading-[24px] uppercase tracking-widest font-semibold text-sportsBlack">Total to pay</h1>
                        <p className="text-[32px] text-sportsBlack font-semibold">£{(cartPrice/100).toFixed(2)}</p>
                    </div>
                  )}

                  {priceId ? (
                    <EmbeddedCheckoutProvider
                      stripe={stripePromise}
                      options={{ clientSecret: clientSecretSettings.clientSecret }}
                    >
                      <EmbeddedCheckout className="max-h-[80dvh]" />
                    </EmbeddedCheckoutProvider>
                  ) : (
                    <Elements
                        stripe={stripePromise}
                        options={{
                            clientSecret: clientSecretSettings.clientSecret,
                            appearance: stripeCheckoutStyles,
                        }}
                    >
                        <CheckoutForm address="true" amount={(cartPrice/100).toFixed(2)} successURL="/shop/checkout/success" failureURL="/shop/checkout/failure" />
                    </Elements>
                  )}
              </div>
          )}
      </>
    </div>
  );
}